/// <amd-module name="VC/Common/Navigation" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { routeUrl } from "VSS/Platform/Location";
import { IVssNavigationService } from "VSS/Platform/Navigation";

export interface IVCNavigationData {
    defaultGitRepoName: string;
    defaultGitRepoUrl: string;
    defaultRepoIsGit: boolean;
    defaultRepoIsFork: boolean;
    defaultRepoId: string;
    supportsTfvc: boolean;
}

export namespace RouteIds {
    export const Branches = "ms.vss-code-web.branches-route";
    export const BranchesCompare = "ms.vss-code-web.branch-compare-route";

    export const Changeset = "ms.vss-code-web.tfvc-changeset-details-route";
    export const Commit = "ms.vss-code-web.commit-route";
    export const Commits = "ms.vss-code-web.commits-route";
    export const Shelveset = "ms.vss-code-web.tfvc-shelveset-details-route";

    export const TfvcFiles = "ms.vss-code-web.tfvc-files-route";
    export const GitFiles = "ms.vss-code-web.files-route-git";

    export const Pushes = "ms.vss-code-web.pushes-route";
    export const Tags = "ms.vss-code-web.tags-route";

    export const PullRequests = "ms.vss-code-web.pull-request-list-route";
    export const PullRequest = "ms.vss-code-web.pull-request-review-route";
    export const MyPullRequests = "ms.vss-code-web.my-pullrequests-me-page-route";

    export const GitOverview = "ms.vss-code-web.repository-overview-git-route";
    export const TfvcOverview = "ms.vss-code-web.repository-overview-tfvc-route";
}

const DefaultHubRoutes: { [hubId: string]: string } = {
    "ms.vss-code-web.git-repository-overview": RouteIds.GitOverview,
    "ms.vss-code-web.files-hub-git": RouteIds.GitFiles,
    "ms.vss-code-web.branches-hub": RouteIds.Branches,
    "ms.vss-code-web.tags-hub": RouteIds.Tags,
    "ms.vss-code-web.pushes-hub": RouteIds.Pushes,
    "ms.vss-code-web.commits-hub": RouteIds.Commits,
    "ms.vss-code-web.pull-request-hub": RouteIds.PullRequests
};

export interface IVCNavigationService extends IVssService {
    /**
     * Get version control navigation data
     */
    getData: () => IVCNavigationData | undefined;

    /**
     * Get the URL for the given changeset or shelveset
     *
     * @param commitId commit
     * @param projectName Optional name of the team project. Defaults to the current page's project.
     * @param teamName Optional name of the team. Defaults to the current page's team.
     */
    getTfvcCommitUrl(commitId: string, projectName?: string | null, teamName?: string | null): string;

    /**
     * Get the URL for the given repository
     *
     * @param repositoryName Name of the repository. Null for tfvc.
     * @param projectName Optional name of the team project. Defaults to the current page's project.
     * @param teamName Optional name of the team. Defaults to the current page's team.
     * @param routeId Optional specific route id. Defaults to the current hub's primary route.
     */
    getRepositoryUrl(
        repositoryName: string | null,
        projectName?: string | null,
        teamName?: string | null,
        routeId?: string,
        additionalRoutes?: { [key: string]: string }
    ): string;

    /**
     * Get the URL for the given pull request
     *
     * @param pullRequestId The id of the pull request.
     * @param repositoryName Name of the repository. Null for tfvc.
     * @param projectName Optional name of the team project. Defaults to the current page's project.
     * @param teamName Optional name of the team. Defaults to the current page's team.
     */
    getPullRequestUrl(pullRequestId: number, repositoryName?: string | null, projectName?: string | null): string;

    /**
     * Get the URL for the given commit
     *
     * @param commitId commit
     * @param repositoryName Name of the repository
     * @param projectName Optional name of the team project. Defaults to the current page's project.
     * @param teamName Optional name of the team. Defaults to the current page's team.
     */
    getGitCommitUrl(commitId: string, repositoryName: string, projectName?: string | null, teamName?: string | null): string;

    /**
     * Get the URL for the given commit
     *
     * @param baseCommitId base commit
     * @param targetCommitId target commit
     * @param repositoryName Name of the repository
     * @param projectName Optional name of the team project. Defaults to the current page's project.
     * @param teamName Optional name of the team. Defaults to the current page's team.
     */
    getGitCompareCommitsUrl(
        baseCommitId: string,
        targetCommitId: string,
        repositoryName: string,
        projectName?: string | null,
        teamName?: string | null
    ): string;

    /**
     * Get the URL for the given branch or tag
     *
     * @param refName refName eg refs/heads/master or refs/tags/tag1.
     * @param repositoryName Name of the repository. Null for tfvc.
     * @param projectName Optional name of the team project. Defaults to the current page's project.
     * @param teamName Optional name of the team. Defaults to the current page's team.
     */
    getRefUrl(refName: string, repositoryName?: string | null, projectName?: string | null, teamName?: string | null): string;

    /**
     * Get URL for account level pull request list page
     */
    getMyPullRequestsUrl(): string;
}

class VCNavigationService extends VssService implements IVCNavigationService {
    public getData(): IVCNavigationData | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getData<IVCNavigationData>("ms.vss-code-web.navigation-data-provider");
    }

    public getTfvcCommitUrl(commitId: string, projectName?: string | null, teamName?: string | null): string {
        const routeValues: { [key: string]: string } = {};

        this._addDefaultRoutes(routeValues, projectName, teamName);

        let routeId = "";
        switch(commitId.charAt(0)) {
            case 'C':
                routeId = RouteIds.Changeset;
                routeValues["parameters"] = encodeURIComponent(commitId.substring(1));
                break;
            case 'L':
            case 'W':
            case 'D':
                return "";
            default:
                routeId = RouteIds.Shelveset;
                routeValues["ss"] = commitId;
        }

        // Use the location service to generate the URL
        return routeUrl(this.pageContext, routeId, routeValues);
    }

    public getGitCommitUrl(commitId: string, repositoryName?: string | null, projectName?: string | null, teamName?: string | null): string {
        const routeValues: { [key: string]: string } = {};

        this._addDefaultRoutes(routeValues, projectName, teamName);

        if (repositoryName) {
            routeValues["vc.GitRepositoryName"] = repositoryName;
        }

        routeValues["parameters"] = encodeURIComponent(commitId);

        // Use the location service to generate the URL
        return routeUrl(this.pageContext, RouteIds.Commit, routeValues);
    }

    public getRepositoryUrl(
        repositoryName: string | null,
        projectName?: string | null,
        teamName?: string | null,
        routeId?: string,
        additionalRoutes?: { [key: string]: string }
    ): string {
        if (!routeId) {
            if (repositoryName) {
                // Find the default route id for the current hub
                const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
                const currentHubId = navigationService.getDisplayedNavigation()[3];
                routeId = DefaultHubRoutes[currentHubId] || RouteIds.GitFiles;
            } else {
                routeId = RouteIds.TfvcFiles;
            }
        }

        let routeValues: { [key: string]: string } = {};
        this._addDefaultRoutes(routeValues, projectName, teamName);

        // Add the repository name for git repos
        if (repositoryName) {
            routeValues["vc.GitRepositoryName"] = repositoryName;
        }

        if (additionalRoutes) {
            routeValues = { ...additionalRoutes, ...routeValues };
        }

        // Use the location service to generate the URL
        return routeUrl(this.pageContext, routeId, routeValues);
    }

    public getPullRequestUrl(pullRequestId: number, repositoryName?: string | null, projectName?: string | null): string {
        const routeValues: { [key: string]: string } = {};

        this._addDefaultRoutes(routeValues, projectName, null);

        // Add the repository name for git repos
        if (repositoryName) {
            routeValues["vc.GitRepositoryName"] = repositoryName;
        }

        routeValues["parameters"] = pullRequestId.toString();

        // Use the location service to generate the URL
        return routeUrl(this.pageContext, RouteIds.PullRequest, routeValues);
    }

    public getGitCompareCommitsUrl(
        baseCommitId: string,
        targetCommitId: string,
        repositoryName: string,
        projectName?: string | null,
        teamName?: string | null
    ): string {
        let routeValues: { [key: string]: string } = {};

        this._addDefaultRoutes(routeValues, projectName, teamName);

        // Add the repository name for git repos
        if (repositoryName) {
            routeValues["vc.GitRepositoryName"] = repositoryName;
        }

        routeValues["baseVersion"] = "GC" + baseCommitId;
        routeValues["targetVersion"] = "GC" + targetCommitId;
        routeValues["_a"] = "commits";

        return routeUrl(this.pageContext, RouteIds.BranchesCompare, routeValues);
    }

    public getRefUrl(refName: string, repositoryName?: string | null, projectName?: string | null, teamName?: string | null): string {
        let routeId = RouteIds.GitFiles;
        if (!repositoryName) {
            routeId = RouteIds.TfvcFiles;
        }

        let routeValues: { [key: string]: string } = {};

        this._addDefaultRoutes(routeValues, projectName, teamName);

        // Add the repository name for git repos
        if (repositoryName) {
            routeValues["vc.GitRepositoryName"] = repositoryName;
        }

        const branchPrefix = "refs/heads/";
        const tagPrefix = "refs/tags/";
        if (refName.indexOf(branchPrefix) === 0) {
            routeValues["version"] = "GB" + refName.substring(branchPrefix.length);
        } else if (refName.indexOf(tagPrefix) === 0) {
            routeValues["version"] = "GT" + refName.substring(tagPrefix.length);
        } else if (routeId === RouteIds.TfvcFiles) {
            routeValues["path"] = refName;
        }

        // Use the location service to generate the URL
        return routeUrl(this.pageContext, routeId, routeValues);
    }

    public getMyPullRequestsUrl(): string {
        return routeUrl(this.pageContext, RouteIds.MyPullRequests);
    }

    private _addDefaultRoutes(routeValues: { [key: string]: string }, projectName?: string | null, teamName?: string | null) {
        if (!projectName || !teamName) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            const pageProjectName = tfsPageData && tfsPageData.project && tfsPageData.project.name;

            if (projectName === undefined && pageProjectName) {
                projectName = pageProjectName;
            }
        }

        // Compute route values starting with the project name
        if (projectName) {
            routeValues["project"] = projectName;
        }

        // Add the current team name
        if (teamName) {
            routeValues["team"] = teamName;
        }
    }
}

Services.add("IVCNavigationService", { serviceFactory: VCNavigationService });
